import { Box, CircularProgress, Tab } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { getAuditorVisitById } from '../../api/getAuditorVisitById';
import { AuditorVisitDetail, TeamMember, TeamMemberDetail } from '../../types';
import Details from '../../components/Details';
import Team from '../../components/Team';
import MyCosts from '../../components/MyCosts';
import Services from '../../components/Services';
import { CertificateList } from '@/common';
import { AuditorVisitDetailProvider } from '../../contexts/AuditorVisitDetailContext';
import AuditorFiles from '../../components/AuditorFiles';
import AuditorFilesArchive from '../../components/AuditorFilesArchive';
import ReturnedDocs from '../../components/ReturnedDocs';
import VisitProducts from '../../components/VisitProducts';
import NextCloudButton from '@/common/components/NextCloudButton';

const DetailPage = () => {
  const { id } = useParams<{ id: string }>() as { id: string };

  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const query = useQuery<AuditorVisitDetail, ApiError>(
    ['auditor-visits-id', id],
    () => getAuditorVisitById(id, lang),
    { refetchOnWindowFocus: false }
  );

  const isCheckListFullyChecked = useMemo(() => {
    if (!query.data) {
      return true;
    }

    const areAllCheckListItemsChecked = query.data.checkList?.every((e) => e.confDat);

    if (!areAllCheckListItemsChecked) {
      return false;
    }

    return true;
  }, [query.data]);

  if (query.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (query.isError) {
    return <p>{query.error.message}</p>;
  }
  const isDetailedTeamMember = (teamMember: TeamMember): teamMember is TeamMemberDetail => {
    return 'costs' in teamMember && 'files' in teamMember;
  };

  const checkForCosts = (teamMembers?: TeamMember[]): boolean => {
    if (!teamMembers) return false;
    const result = teamMembers.every((member) => {
      const memberDetailed = isDetailedTeamMember(member);
      if (memberDetailed) {
        return member.costs.every((cost) => !cost.isConfirmedByLeader);
      }
      return false;
    });
    return result;
  };
  return (
    <Box sx={{ mt: 1 }}>
      <AuditorVisitDetailProvider auditorVisit={query.data}>
        <DetailView
          childrenSx={{ padding: '2rem' }}
          title={
            <DetailView.Title>{`${t('Customer name', { defaultValue: 'Customer name' })}:  ${
              query.data.workOrder?.name || 'N/A'
            } | ${t('Visit', { defaultValue: 'Visit' })}: ${
              query.data.uId || 'N/A'
            }`}</DetailView.Title>
          }
          onGoBack={() => navigate('/auditor-visits')}
          tabs={[
            { id: 'details', title: t('Details', { defaultValue: 'Details' }) },
            { id: 'services', title: t('Services', { defaultValue: 'Services' }) },
            {
              id: 'team',
              title: t('Team', { defaultValue: 'Team' }),
              showNotification: checkForCosts(query.data.teamMembers),
            },
            { id: 'myCosts', title: t('My costs', { defaultValue: 'My costs' }) },

            {
              id: 'returnedDocs',
              title: t('Returned documentation', { defaultValue: 'Returned documentation' }),
              showNotification: !isCheckListFullyChecked,
            },
            { id: 'certificates', title: t('Certificates', { defaultValue: 'Certificates' }) },

            { id: 'files', title: t('Documents', { defaultValue: 'Documents' }) },
            {
              id: 'files-archive',
              title: t('Documents archive', { defaultValue: 'Documents archive' }),
            },
            {
              id: 'products-nextcloud',
              title: t('Dokumentacija produktov', {
                defaultValue: 'Dokumentacija produktov',
              }),
            },
          ]}
          defaultTab="details"
          customTabs={[
            <Tab
              key="nextcloud"
              sx={{
                height: '3rem',
                minHeight: 0,
                flexGrow: 1,
              }}
              icon={
                <NextCloudButton
                  visitUid={query.data?.uId || ''}
                  woUid={query.data.workOrder?.uId || ''}
                  label="auditor"
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />,
          ]}
        >
          {({ TabContent }) => (
            <>
              <TabContent tab="details">
                <Details />
              </TabContent>
              <TabContent tab="team">
                <Team />
              </TabContent>
              <TabContent tab="myCosts">
                <MyCosts />
              </TabContent>
              <TabContent tab="services">
                <Services />
              </TabContent>
              {/* <TabContent tab="reports">
                <Reports />
              </TabContent> */}
              <TabContent tab="certificates">
                <CertificateList certificates={query.data.certificates} visitId={query.data.id} />
              </TabContent>
              <TabContent tab="returnedDocs">
                <ReturnedDocs />
              </TabContent>
              <TabContent tab="files">
                <AuditorFiles visit={query.data} />
              </TabContent>
              <TabContent tab="files-archive">
                <AuditorFilesArchive visitId={query.data.id} woUid={query.data.workOrder?.uId} />
              </TabContent>
              <TabContent tab="products-nextcloud">
                <VisitProducts visitId={query.data.id} woUid={query.data.workOrder?.uId} />
              </TabContent>
            </>
          )}
        </DetailView>
      </AuditorVisitDetailProvider>
    </Box>
  );
};

export default DetailPage;
